/**
 * @file TrakcjaProjectCard.tsx
 * @description Presentational card for one project on the "Trakcja" board. Collapsed it is a
 * dense row (left momentum bar, name, type badge, "co teraz", blocker/decision mini-counters,
 * time-ago). Expanded it shows the health line (momentum_reason + owner/sprint/money), five
 * kind-grouped sections (Cele / Założenia / Co się udało / Co nie wyszło / Blokery — only
 * Blokery open by default), per-item evidence on demand, a checkbox that resolves a blocker,
 * and a "Do Hermesa" button that promotes a next-step / blocker into a real life-layer task.
 * All copy is intentionally Polish (personal instance convention). Purely presentational —
 * expansion, resolve and add-to-Hermes are driven by parent callbacks.
 */

import { useState } from "react";
import {
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  HelpCircle,
  Info,
  Plus,
  Target,
  XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type {
  NewLifeTaskFields,
  TrakcjaItem,
  TrakcjaItemKind,
  TrakcjaItemStatus,
  TrakcjaProject,
} from "../lib/api";

const MOMENTUM_DOT: Record<TrakcjaProject["momentum"], string> = {
  GREEN: "bg-emerald-400",
  YELLOW: "bg-amber-400",
  RED: "bg-red-400",
};

const KIND_META: Record<TrakcjaItemKind, { icon: LucideIcon; label: string; cls: string }> = {
  GOAL: { icon: Target, label: "Cele", cls: "text-accent" },
  ASSUMPTION: { icon: HelpCircle, label: "Założenia / rozkminy", cls: "text-gray-400" },
  WIN: { icon: CheckCircle2, label: "Co się udało", cls: "text-emerald-400" },
  LOSS: { icon: XCircle, label: "Co nie wyszło", cls: "text-red-400" },
  BLOCKER: { icon: Ban, label: "Blokery", cls: "text-amber-400" },
};

const KIND_ORDER: TrakcjaItemKind[] = ["BLOCKER", "GOAL", "ASSUMPTION", "WIN", "LOSS"];

const STATUS_BADGE: Record<TrakcjaItemStatus, string> = {
  OPEN: "text-gray-400 border-border",
  DONE: "text-emerald-400 border-emerald-500/30",
  VALIDATED: "text-emerald-400 border-emerald-500/30",
  INVALIDATED: "text-red-400 border-red-500/30",
  BLOCKED: "text-amber-400 border-amber-500/30",
};

const KNOWN = (v: string | null): v is string =>
  Boolean(v && v.trim() && v.trim().toLowerCase() !== "brak danych");

/** "3 dni temu" style age; null/"brak danych" → null. */
function ageLabel(iso: string | null): { label: string; stale: boolean } | null {
  if (!KNOWN(iso)) return null;
  const ms = Date.parse(iso as string);
  if (Number.isNaN(ms)) return null;
  const days = Math.floor((Date.now() - ms) / 86_400_000);
  const stale = days > 12;
  if (days <= 0) return { label: "dziś", stale };
  if (days === 1) return { label: "1 dzień temu", stale };
  if (days < 31) return { label: `${days} dni temu`, stale };
  const months = Math.floor(days / 30);
  return { label: months === 1 ? "1 mies. temu" : `${months} mies. temu`, stale };
}

interface ItemRowProps {
  item: TrakcjaItem;
  busy: boolean;
  added: boolean;
  onResolve?: () => void;
  onAddToHermes?: () => void;
}

function ItemRow({ item, busy, added, onResolve, onAddToHermes }: ItemRowProps) {
  const [showEvidence, setShowEvidence] = useState(false);
  const isBlocker = item.kind === "BLOCKER";
  const done = item.status === "DONE";
  return (
    <div className="flex items-start gap-2 py-1.5">
      {isBlocker && onResolve && (
        <button
          type="button"
          onClick={onResolve}
          disabled={busy || done}
          aria-label={done ? "Załatwione" : "Oznacz jako załatwione"}
          title={done ? "Załatwione" : "Oznacz jako załatwione"}
          className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-60 ${
            done
              ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-400"
              : "border-border bg-surface-2 text-transparent hover:border-emerald-500/40"
          }`}
        >
          <CheckCircle2 className="w-3 h-3" />
        </button>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <p
            className={`text-xs text-gray-300 min-w-0 ${done ? "line-through text-gray-500" : ""}`}
          >
            {item.text}
          </p>
          <span
            className={`ml-auto flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded border ${STATUS_BADGE[item.status]}`}
          >
            {item.status}
          </span>
          {KNOWN(item.evidence) && (
            <button
              type="button"
              onClick={() => setShowEvidence((s) => !s)}
              aria-label="Pokaż dowód"
              title="Pokaż dowód"
              className="flex-shrink-0 text-gray-500 hover:text-gray-300 transition-colors"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          )}
          {onAddToHermes && (
            <button
              type="button"
              onClick={onAddToHermes}
              disabled={busy || added}
              title={added ? "Dodane do Hermesa" : "Dodaj jako zadanie Hermes"}
              className={`flex-shrink-0 text-[10px] font-medium inline-flex items-center gap-1 px-1.5 py-0.5 rounded border transition-colors disabled:opacity-70 ${
                added
                  ? "text-emerald-400 border-emerald-500/30"
                  : "text-gray-400 border-border hover:text-gray-200 hover:bg-surface-3"
              }`}
            >
              {added ? <CheckCircle2 className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
              {added ? "W Hermesie" : "Do Hermesa"}
            </button>
          )}
        </div>
        {showEvidence && KNOWN(item.evidence) && (
          <p className="mt-1 text-[11px] font-mono text-gray-500 break-words">{item.evidence}</p>
        )}
      </div>
    </div>
  );
}

interface TrakcjaProjectCardProps {
  project: TrakcjaProject;
  expanded: boolean;
  busyId: string | null;
  addedKeys: Set<string>;
  onToggleExpand: (id: string) => void;
  onResolveItem: (itemId: string) => void;
  onAddToHermes: (fields: NewLifeTaskFields, key: string) => void;
}

export function TrakcjaProjectCard({
  project,
  expanded,
  busyId,
  addedKeys,
  onToggleExpand,
  onResolveItem,
  onAddToHermes,
}: TrakcjaProjectCardProps) {
  const [openKinds, setOpenKinds] = useState<Set<TrakcjaItemKind>>(new Set(["BLOCKER"]));
  const blockers = project.items.filter((i) => i.kind === "BLOCKER" && i.status !== "DONE").length;
  const wins = project.items.filter((i) => i.kind === "WIN").length;
  const age = ageLabel(project.last_activity_at);

  const hermesPriority = project.momentum === "RED" ? "HIGH" : "MEDIUM";
  const hermesDue = KNOWN(project.deadline_at) ? project.deadline_at : null;
  const addFromText = (text: string, key: string, evidence?: string | null) =>
    onAddToHermes(
      {
        title: text,
        notes: `Z Trakcji: ${project.name}${KNOWN(evidence ?? null) ? ` · ${evidence}` : ""}`,
        priority: hermesPriority,
        dueAt: hermesDue,
      },
      key
    );

  const toggleKind = (k: TrakcjaItemKind) =>
    setOpenKinds((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });

  return (
    <div className="card overflow-hidden">
      {/* Collapsed row */}
      <button
        type="button"
        onClick={() => onToggleExpand(project.id)}
        className="w-full flex items-center gap-3 p-2.5 text-left hover:bg-surface-3/60 transition-colors"
      >
        <span
          className={`w-1 self-stretch rounded-full flex-shrink-0 ${MOMENTUM_DOT[project.momentum]}`}
          aria-hidden
        />
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
        )}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-100 truncate">{project.name}</span>
            <span
              className={`flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded border ${
                project.type === "CLIENT"
                  ? "text-accent border-accent/30 bg-accent/10"
                  : "text-gray-400 border-border"
              }`}
            >
              {project.type === "CLIENT" ? "Klient" : "Apka"}
            </span>
          </span>
          {KNOWN(project.current_goal) && (
            <span className="block text-xs text-gray-400 truncate mt-0.5">
              {project.current_goal}
            </span>
          )}
        </span>
        <span className="hidden md:flex items-center gap-2 flex-shrink-0">
          {blockers > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] text-amber-400">
              <Ban className="w-3 h-3" />
              {blockers}
            </span>
          )}
          {age && (
            <span
              className={`inline-flex items-center gap-1 text-[11px] ${age.stale ? "text-red-400" : "text-gray-500"}`}
            >
              <Clock className="w-3 h-3" />
              {age.label}
            </span>
          )}
        </span>
      </button>

      {/* Expanded panel */}
      {expanded && (
        <div className="animate-slide-up border-t border-border px-3 py-3 space-y-3 bg-surface-2/40">
          {/* Health line */}
          <div className="space-y-1">
            {KNOWN(project.momentum_reason) && (
              <p className="text-xs text-gray-400 italic">{project.momentum_reason}</p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500">
              {KNOWN(project.stage) && (
                <span>
                  Etap: <span className="text-gray-300">{project.stage}</span>
                </span>
              )}
              {KNOWN(project.deadline_at) && (
                <span>
                  Termin: <span className="text-gray-300">{project.deadline_at}</span>
                </span>
              )}
              {KNOWN(project.money_note) && (
                <span>
                  Kasa: <span className="text-gray-300">{project.money_note}</span>
                </span>
              )}
              {KNOWN(project.owner) && (
                <span>
                  Właściciel: <span className="text-gray-300">{project.owner}</span>
                </span>
              )}
            </div>
          </div>

          {/* Next step — promotable to Hermes */}
          {KNOWN(project.next_step) && (
            <div className="rounded-lg bg-surface-2 border border-border p-2.5 flex items-start gap-2">
              <Target className="w-3.5 h-3.5 text-accent mt-0.5 flex-shrink-0" />
              <p className="text-xs text-gray-200 min-w-0 flex-1">
                <span className="text-gray-500">Następny krok: </span>
                {project.next_step}
              </p>
              <button
                type="button"
                onClick={() => addFromText(project.next_step as string, `${project.id}:next`)}
                disabled={addedKeys.has(`${project.id}:next`)}
                className={`flex-shrink-0 text-[10px] font-medium inline-flex items-center gap-1 px-1.5 py-0.5 rounded border transition-colors disabled:opacity-70 ${
                  addedKeys.has(`${project.id}:next`)
                    ? "text-emerald-400 border-emerald-500/30"
                    : "text-gray-400 border-border hover:text-gray-200 hover:bg-surface-3"
                }`}
              >
                {addedKeys.has(`${project.id}:next`) ? (
                  <CheckCircle2 className="w-3 h-3" />
                ) : (
                  <Plus className="w-3 h-3" />
                )}
                {addedKeys.has(`${project.id}:next`) ? "W Hermesie" : "Do Hermesa"}
              </button>
            </div>
          )}

          {/* Kind-grouped sections */}
          <div className="space-y-1.5">
            {KIND_ORDER.map((kind) => {
              const items = project.items.filter((i) => i.kind === kind);
              if (items.length === 0) return null;
              const meta = KIND_META[kind];
              const Icon = meta.icon;
              const open = openKinds.has(kind);
              return (
                <div key={kind} className="rounded-lg border border-border/60">
                  <button
                    type="button"
                    onClick={() => toggleKind(kind)}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-surface-3/40 transition-colors"
                  >
                    {open ? (
                      <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
                    )}
                    <Icon className={`w-3.5 h-3.5 ${meta.cls}`} />
                    <span className="text-xs font-medium text-gray-200">{meta.label}</span>
                    <span className="text-[11px] text-gray-500">{items.length}</span>
                  </button>
                  {open && (
                    <div className="px-2.5 pb-1.5 divide-y divide-border/40">
                      {items.map((item) => {
                        const addKey = `${project.id}:${item.id}`;
                        const canAdd = kind === "BLOCKER" || kind === "GOAL";
                        return (
                          <ItemRow
                            key={item.id}
                            item={item}
                            busy={busyId === item.id}
                            added={addedKeys.has(addKey)}
                            onResolve={
                              kind === "BLOCKER" ? () => onResolveItem(item.id) : undefined
                            }
                            onAddToHermes={
                              canAdd
                                ? () => addFromText(item.text, addKey, item.evidence)
                                : undefined
                            }
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Data gaps */}
          {project.data_gaps.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {project.data_gaps.map((gap, i) => (
                <span
                  key={i}
                  className="text-[10px] text-amber-400/80 bg-amber-500/5 border border-amber-500/20 rounded px-1.5 py-0.5"
                  title={gap}
                >
                  brak danych: {gap.length > 48 ? `${gap.slice(0, 48)}…` : gap}
                </span>
              ))}
            </div>
          )}

          {wins > 0 && project.source_freshness && (
            <p className="text-[10px] text-gray-600 pt-1">
              {wins} sukcesów · świeżość {project.source_freshness} · {project.sources_used.length}{" "}
              źródeł
            </p>
          )}
        </div>
      )}
    </div>
  );
}
